import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  getBpeCidades,
  consultarViagens,
  getAssentos,
  preReserva,
  confirmarReserva,
} from "../../api/bpeApi";

const steps = ["Consultar rotas", "Selecionar viagem", "Selecionar assentos", "Dados dos passageiros", "Conclusão"];

function formatDateForInput(str) {
  if (!str) return "";
  const s = String(str).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
  const [d, m, y] = s.split(/[/-]/);
  if (d && m && y) return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  return s;
}

function parsePostback(postback) {
  if (typeof postback === "object" && postback?.id) return postback;
  if (typeof postback === "string" && postback.includes("##CIDADE##")) {
    const parts = postback.replace("##CIDADE##", "").split("#");
    return { id: parts[0], id_interno: parts[1], nome: parts[2], uf: parts[3], fonte: parts[4] };
  }
  return null;
}

const BPeWizard = ({
  open,
  onClose,
  conversationId,
  onSuccess,
  initialOrigem = "",
  initialDestino = "",
  initialDataViagem = null,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [cidadesOrigem, setCidadesOrigem] = useState([]);
  const [cidadesDestino, setCidadesDestino] = useState([]);
  const [origem, setOrigem] = useState("");
  const [destino, setDestino] = useState("");
  const [dataViagem, setDataViagem] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [quantidadePassageiros, setQuantidadePassageiros] = useState(1);

  const [viagens, setViagens] = useState([]);
  const [opcaoViagem, setOpcaoViagem] = useState("");
  const [assentos, setAssentos] = useState({ disponiveis: [], selected: {} });
  const [passageiros, setPassageiros] = useState([{ nome: "", documento: "", email: "", celular: "" }]);
  const [reservationIds, setReservationIds] = useState([]);
  const [resultado, setResultado] = useState(null);

  const loadCidadesOrigem = useCallback(async () => {
    const list = await getBpeCidades("origem");
    setCidadesOrigem(Array.isArray(list) ? list : []);
  }, []);
  const loadCidadesDestino = useCallback(async (origemId) => {
    const list = await getBpeCidades("destino", origemId);
    setCidadesDestino(Array.isArray(list) ? list : []);
  }, []);

  React.useEffect(() => {
    if (open && conversationId) {
      loadCidadesOrigem();
    }
  }, [open, conversationId, loadCidadesOrigem]);

  React.useEffect(() => {
    if (!open) return;
    if (initialOrigem) setOrigem(String(initialOrigem));
    if (initialDestino) setDestino(String(initialDestino));
    if (initialDataViagem) {
      const f = formatDateForInput(initialDataViagem);
      if (f) setDataViagem(f);
    }
  }, [open, initialOrigem, initialDestino, initialDataViagem]);
  React.useEffect(() => {
    const o = parsePostback(origem);
    if (o?.id) loadCidadesDestino(Number(o.id));
    else setCidadesDestino([]);
  }, [origem, loadCidadesDestino]);

  const handleConsultar = async () => {
    setError(null);
    setLoading(true);
    try {
      const o = parsePostback(origem);
      const d = parsePostback(destino);
      if (!o || !d) {
        setError("Selecione origem e destino.");
        return;
      }
      const dataStr = dataViagem.includes("-") ? dataViagem.split("-").reverse().join("/") : dataViagem;
      const result = await consultarViagens(conversationId, {
        origem: o,
        destino: d,
        data_viagem: dataStr,
        quantidade_passageiros: quantidadePassageiros,
      });
      setViagens(result.viagens || []);
      if ((result.viagens || []).length === 0) {
        setError("Nenhuma viagem encontrada para esta data e trecho.");
        return;
      }
      setActiveStep(1);
      setOpcaoViagem("");
    } catch (e) {
      setError(e.message || "Erro ao consultar viagens.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelecionarViagem = () => {
    if (!opcaoViagem) {
      setError("Selecione uma viagem.");
      return;
    }
    setError(null);
    setActiveStep(2);
    setAssentos({ disponiveis: [], selected: {} });
  };

  const loadAssentos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAssentos(conversationId, Number(opcaoViagem));
      setAssentos((prev) => ({
        ...prev,
        disponiveis: result.assentosdisponiveis || [],
        selected: {},
      }));
    } catch (e) {
      setError(e.message || "Erro ao carregar assentos.");
    } finally {
      setLoading(false);
    }
  }, [conversationId, opcaoViagem]);

  React.useEffect(() => {
    if (activeStep === 2 && opcaoViagem) loadAssentos();
  }, [activeStep, opcaoViagem, loadAssentos]);

  const handleAssentosContinuar = async () => {
    const sel = assentos.selected;
    const qtd = quantidadePassageiros;
    const missing = [];
    for (let i = 0; i < qtd; i++) {
      if (!sel[i]) missing.push(i + 1);
    }
    if (missing.length) {
      setError(`Selecione a poltrona do passageiro ${missing.join(", ")}.`);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const ids = [];
      for (let i = 0; i < qtd; i++) {
        const r = await preReserva(conversationId, { poltrona: sel[i], index: i });
        if (r.reservationId) ids.push(r.reservationId);
      }
      setReservationIds(ids);
      setPassageiros(Array.from({ length: qtd }, () => ({ nome: "", documento: "", email: "", celular: "" })));
      setActiveStep(3);
    } catch (e) {
      setError(e.message || "Erro na pré-reserva.");
    } finally {
      setLoading(false);
    }
  };

  const handlePassageirosChange = (index, field, value) => {
    setPassageiros((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleConfirmarReserva = async () => {
    const invalid = passageiros.findIndex((p) => !p.nome?.trim() || !p.documento?.trim());
    if (invalid >= 0) {
      setError("Preencha nome e documento de todos os passageiros.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const results = [];
      for (let i = 0; i < passageiros.length; i++) {
        const r = await confirmarReserva(conversationId, {
          passageiro_index: i,
          ...passageiros[i],
        });
        results.push(r);
      }
      setResultado(results);
      setActiveStep(4);
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(e.message || "Erro ao emitir BPe.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setError(null);
    setViagens([]);
    setOpcaoViagem("");
    setAssentos({ disponiveis: [], selected: {} });
    setPassageiros([{ nome: "", documento: "", email: "", celular: "" }]);
    setResultado(null);
    onClose();
  };

  if (!conversationId) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Emitir BPe</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Origem</InputLabel>
              <Select
                value={origem}
                onChange={(e) => setOrigem(e.target.value)}
                label="Origem"
                displayEmpty
              >
                <MenuItem value="">Selecione a origem</MenuItem>
                {cidadesOrigem.map((c) => (
                  <MenuItem key={c.id} value={c.postback || c.id}>
                    {c.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Destino</InputLabel>
              <Select
                value={destino}
                onChange={(e) => setDestino(e.target.value)}
                label="Destino"
                displayEmpty
              >
                <MenuItem value="">Selecione o destino</MenuItem>
                {cidadesDestino.map((c) => (
                  <MenuItem key={c.id} value={c.postback || c.id}>
                    {c.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Data da viagem"
              type="date"
              value={formatDateForInput(dataViagem)}
              onChange={(e) => setDataViagem(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Quantidade de passageiros"
              type="number"
              inputProps={{ min: 1, max: 5 }}
              value={quantidadePassageiros}
              onChange={(e) => setQuantidadePassageiros(Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 1)))}
              fullWidth
            />
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Selecione a viagem</Typography>
            <RadioGroup value={opcaoViagem} onChange={(e) => setOpcaoViagem(e.target.value)}>
              {viagens.map((v, i) => (
                <FormControlLabel
                  key={v.id || i}
                  value={String(i + 1)}
                  control={<Radio />}
                  label={`Opção ${i + 1}: ${v.inicio} - ${v.fim} | R$ ${v.valor} | ${v.viacao} | ${v.poltronas_disponiveis} poltronas`}
                />
              ))}
            </RadioGroup>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Selecione a poltrona de cada passageiro</Typography>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              Array.from({ length: quantidadePassageiros }, (_, i) => (
                <FormControl key={i} fullWidth sx={{ mt: 1 }}>
                  <InputLabel>Passageiro {i + 1}</InputLabel>
                  <Select
                    value={assentos.selected[i] || ""}
                    onChange={(e) =>
                      setAssentos((prev) => ({
                        ...prev,
                        selected: { ...prev.selected, [i]: e.target.value },
                      }))
                    }
                    label={`Passageiro ${i + 1}`}
                  >
                    <MenuItem value="">Selecione</MenuItem>
                    {(assentos.disponiveis || []).map((num) => (
                      <MenuItem key={num} value={num}>
                        {num}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))
            )}
          </Box>
        )}

        {activeStep === 3 && (
          <Box display="flex" flexDirection="column" gap={2}>
            {passageiros.map((p, i) => (
              <Box key={i} sx={{ border: 1, borderColor: "divider", p: 1.5, borderRadius: 1 }}>
                <Typography variant="subtitle2">Passageiro {i + 1}</Typography>
                <TextField
                  label="Nome"
                  value={p.nome}
                  onChange={(e) => handlePassageirosChange(i, "nome", e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  required
                />
                <TextField
                  label="Documento (CPF/RG)"
                  value={p.documento}
                  onChange={(e) => handlePassageirosChange(i, "documento", e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  required
                />
                <TextField
                  label="E-mail"
                  value={p.email}
                  onChange={(e) => handlePassageirosChange(i, "email", e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                />
                <TextField
                  label="Celular"
                  value={p.celular}
                  onChange={(e) => handlePassageirosChange(i, "celular", e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>
            ))}
          </Box>
        )}

        {activeStep === 4 && resultado && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              BPe(s) emitido(s) com sucesso.
            </Alert>
            {resultado.map((r, i) => (
              <Typography key={i} variant="body2">
                Localizador: {r.dados_bpe?.Localizador || r.reservationId} | Nº BPe: {r.dados_bpe?.NumeroBpe}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep < 4 && (
          <Button onClick={handleClose}>Cancelar</Button>
        )}
        {activeStep === 0 && (
          <Button variant="contained" onClick={handleConsultar} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Consultar"}
          </Button>
        )}
        {activeStep === 1 && (
          <Button variant="contained" onClick={handleSelecionarViagem} disabled={!opcaoViagem}>
            Continuar
          </Button>
        )}
        {activeStep === 2 && (
          <Button variant="contained" onClick={handleAssentosContinuar} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Continuar"}
          </Button>
        )}
        {activeStep === 3 && (
          <Button variant="contained" onClick={handleConfirmarReserva} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Emitir BPe"}
          </Button>
        )}
        {activeStep === 4 && (
          <Button variant="contained" onClick={handleClose}>
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BPeWizard;
